import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiGetChampions } from '../../api/beltApi';
import type { CurrentBeltChampion, CurrentChampionWrestler } from '../../api/beltApi';

const toSrc = (path: string | null): string | null => {
  if (!path) return null;
  return path.startsWith('http') ? path : (process.env.REACT_APP_STORAGE_URL ?? '') + path;
};

const formatDate = (dateStr: string | null): string | null => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const Section = styled.section<{ $isMobile: boolean }>`
  width: ${({ $isMobile }) => $isMobile ? '100%' : '80%'};
  margin: 0 0 ${({ $isMobile }) => $isMobile ? '28px' : '44px'};
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.16em;
  white-space: nowrap;
`;

const CardsGrid = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-wrap: ${({ $isMobile }) => $isMobile ? 'nowrap' : 'wrap'};
  justify-content: ${({ $isMobile }) => $isMobile ? 'flex-start' : 'center'};
  gap: 12px;
  ${({ $isMobile }) => $isMobile && `
    overflow-x: auto;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  `}
`;

const Card = styled.div<{ $vacant: boolean }>`
  position: relative;
  width: 150px;
  flex-shrink: 0;
  cursor: pointer;
  background: linear-gradient(
    160deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border: 1px solid ${({ $vacant }) =>
    $vacant ? 'rgba(255,255,255,0.08)' : 'rgba(255,215,0,0.2)'};
  border-radius: 14px;
  padding: 18px 14px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  transition: border-color 0.22s, transform 0.22s, box-shadow 0.22s;

  &:hover {
    border-color: ${({ $vacant }) =>
      $vacant ? 'rgba(255,255,255,0.14)' : 'rgba(255,215,0,0.4)'};
    transform: translateY(-4px);
    box-shadow: ${({ $vacant }) =>
      $vacant ? 'none' : '0 8px 28px rgba(255,215,0,0.08)'};
  }
`;

const BeltImgWrap = styled.div`
  width: 100%;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BeltImg = styled.img`
  max-width: 100%;
  max-height: 70px;
  object-fit: contain;
`;

const BeltImgFallback = styled.div`
  font-size: 32px;
  line-height: 1;
`;

const BeltName = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.88);
  text-align: center;
  line-height: 1.35;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.07);
`;

const VacantLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.28);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 4px 0;
`;

const AvatarRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
`;

const AvatarCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(255, 215, 0, 0.35);
  background: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
`;

const ChampionName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  text-align: center;
  line-height: 1.3;
`;

const ShowInfo = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.25);
  text-align: center;
  line-height: 1.5;
`;

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const SkeletonBlock = styled.div<{ $w?: string; $h: string; $radius?: string }>`
  ${shimmer}
  width: ${({ $w }) => $w ?? '100%'};
  height: ${({ $h }) => $h};
  border-radius: ${({ $radius }) => $radius ?? '6px'};
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0.06) 25%,
    rgba(255,255,255,0.12) 50%,
    rgba(255,255,255,0.06) 75%
  );
  background-size: 800px 100%;
  animation: shimmer 1.4s infinite linear;
  flex-shrink: 0;
`;

const SkeletonCard = styled(Card)`
  pointer-events: none;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  &::after {
    content: '';
    width: 28px;
    height: 28px;
    border: 3px solid rgba(255, 215, 0, 0.25);
    border-top-color: gold;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
`;

const CardSkeleton: React.FC = () => (
  <SkeletonCard $vacant={false}>
    <BeltImgWrap>
      <SkeletonBlock $h="60px" $radius="8px" />
    </BeltImgWrap>
    <SkeletonBlock $h="14px" $w="80%" />
    <Divider />
    <AvatarRow>
      <SkeletonBlock $w="38px" $h="38px" $radius="50%" />
    </AvatarRow>
    <SkeletonBlock $h="13px" $w="70%" />
    <SkeletonBlock $h="10px" $w="55%" />
  </SkeletonCard>
);

// ─── BeltCard ─────────────────────────────────────────────────────────────────

const BeltCard: React.FC<{ belt: CurrentBeltChampion }> = ({ belt }) => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const beltSrc = toSrc(belt.img_path);
  const names = belt.champions.map((c) => c.name).join(' & ');
  const date = formatDate(belt.date_win);

  const href = belt.is_team
    ? `/belts/tag/${belt.belt_id}/hall-of-fame`
    : `/belts/${belt.belt_id}/hall-of-fame`;

  const handleClick = () => {
    setIsNavigating(true);
    navigate(href);
  };

  return (
    <Card $vacant={belt.is_vacant} onClick={handleClick}>
      {isNavigating && <CardOverlay />}
      <BeltImgWrap>
        {beltSrc
          ? <BeltImg src={beltSrc} alt={belt.name} />
          : <BeltImgFallback>🏆</BeltImgFallback>}
      </BeltImgWrap>

      <BeltName>{belt.name}</BeltName>

      <Divider />

      {belt.is_vacant ? (
        <VacantLabel>Vacante</VacantLabel>
      ) : (
        <>
          {belt.champions.length > 0 && (
            <AvatarRow>
              {belt.champions.map((c: CurrentChampionWrestler) => {
                const src = toSrc(c.avatar_path);
                return (
                  <AvatarCircle key={c.id}>
                    {src && <img src={src} alt={c.name} />}
                  </AvatarCircle>
                );
              })}
            </AvatarRow>
          )}
          {names && <ChampionName>{names}</ChampionName>}
          {(belt.show_name || date) && (
            <ShowInfo>
              {belt.show_name && <span>{belt.show_name}</span>}
              {belt.show_name && date && <br />}
              {date && <span>{date}</span>}
            </ShowInfo>
          )}
        </>
      )}
    </Card>
  );
};

// ─── ChampionsSection ─────────────────────────────────────────────────────────

const ChampionsSection: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  const [belts, setBelts] = useState<CurrentBeltChampion[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    apiGetChampions()
      .then((res) => { if (isMounted) setBelts(res?.data ?? []); })
      .catch(() => {})
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  if (!isLoading && belts.length === 0) return null;

  return (
    <Section $isMobile={isMobile}>
      <SectionHeading>
        <SectionTitle>Campioni Attuali</SectionTitle>
      </SectionHeading>
      <CardsGrid $isMobile={isMobile}>
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)
          : belts.map((belt) => <BeltCard key={belt.belt_id} belt={belt} />)}
      </CardsGrid>
    </Section>
  );
};

export default ChampionsSection;
